/**
 * `<SampleDataBanner>` — thin above-chrome strip inviting recruiters to
 * delete the seeded sample data once they're ready to add their own.
 *
 * Wires the existing Batch 18 endpoint via ``useDeleteSampleData``.
 * Visibility: rendered only when ``hasSampleData`` is true (parent decides
 * by checking ``candidates.some(c => c.is_sample)``).
 *
 * Batch 9 — Dashboard canon strip-down. Sample-data is NOT prototype canon;
 * to keep the canon flow (PageHeader → cards → panels) clean we render this
 * banner ABOVE the PageHeader band via the AboveChromeSlot portal (the
 * Dashboard wraps this in ``<AboveChromeSlot>``). Edge-to-edge, no rounded
 * corners, thin vertical padding — feels like chrome, not content.
 *
 * web-designer §6: outlined button (not filled — sample data isn't an
 * emergency); SparklesIcon prefix (not warning triangle).
 */
import { useState } from "react";
import { useDeleteSampleData } from "@/hooks/queries/candidates/useDeleteSampleData";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  hasSampleData: boolean;
}

const SparklesIcon = ({ className }: { className: string }): JSX.Element => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" />
    <path d="M19 4 L19.6 5.4 L21 6 L19.6 6.6 L19 8 L18.4 6.6 L17 6 L18.4 5.4 Z" />
  </svg>
);

export function SampleDataBanner({ hasSampleData }: Props): JSX.Element | null {
  const mutation = useDeleteSampleData();
  const [confirming, setConfirming] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  if (!hasSampleData) return null;

  const onClick = (): void => {
    if (!confirming) {
      setConfirming(true);
      // Auto-clear confirm prompt after 4s if user doesn't click again.
      setTimeout(() => setConfirming(false), 4000);
      return;
    }
    mutation.mutate(undefined, {
      onSuccess: (r) => {
        addToast(
          `Sample data deleted (${r.rows_affected.candidates} candidates, ${r.rows_affected.jds} jobs)`,
          "success",
        );
        setConfirming(false);
      },
      onError: (err) => {
        addToast(err.message || "Couldn't delete sample data. Try again.", "error");
        setConfirming(false);
      },
    });
  };

  return (
    <div
      data-testid="sample-data-banner"
      className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-content-px py-1.5 text-xs text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"
    >
      <SparklesIcon className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <p className="flex-1">
        <span className="font-semibold">Sample data —</span>{" "}
        delete when you&rsquo;re ready to add your own.
      </p>
      <button
        type="button"
        onClick={onClick}
        disabled={mutation.isPending}
        className="rounded-md border border-amber-300 bg-white/60 px-2.5 py-0.5 text-xs font-semibold text-amber-900 hover:border-amber-400 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-100 dark:hover:bg-amber-900/30"
      >
        {mutation.isPending
          ? "Deleting…"
          : confirming
            ? "Click again to confirm"
            : "Delete sample data"}
      </button>
    </div>
  );
}
